import React, { useEffect, useRef } from 'react';
import { BuildingWithConvertedAddresses, LatLng } from '../../utils/types';
import { centroidOf } from '../../HouseMap/sideAddresses';
import { getBuildingLikeCoordinates } from '../../utils/buildingLike';
import './StreetView.scss';
import LoadingSpinner from '../../common/LoadingSpinner';

interface StreetViewProps {
  selectedBuilding: BuildingWithConvertedAddresses;
}

// Script tag ids: the shared one is owned by loadGoogleMapsApi (mounted by
// GoogleMapsApiProvider at app boot); the other is this component's own
// fallback for when no shared load exists. Either one means the API is on its
// way and injecting another copy would load it twice.
const SHARED_MAPS_SCRIPT_ID = 'google-maps-script';
const OWN_MAPS_SCRIPT_ID = 'street-view-google-maps-script';
const OWN_MAPS_SCRIPT_ERROR_EVENT = 'street-view-google-maps-script-error';
const API_READY_POLL_MS = 100;
const API_READY_TIMEOUT_MS = 15000;

/**
 * Computes the heading (bearing) in degrees from one geographic point to another.
 * This is used to point the Street View camera toward the building.
 *
 * @param from - The starting point (panorama location)
 * @param to - The target point (building centroid)
 * @returns Heading in degrees (0-360), where 0 is north
 */
function computeHeadingToTarget(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): number {
  const fromLatRad = (from.lat * Math.PI) / 180;
  const toLatRad = (to.lat * Math.PI) / 180;
  const dLngRad = ((to.lng - from.lng) * Math.PI) / 180;

  const x = Math.sin(dLngRad) * Math.cos(toLatRad);
  const y =
    Math.cos(fromLatRad) * Math.sin(toLatRad) -
    Math.sin(fromLatRad) * Math.cos(toLatRad) * Math.cos(dLngRad);

  const headingRad = Math.atan2(x, y);
  const headingDeg = (headingRad * 180) / Math.PI;

  return (headingDeg + 360) % 360; // Normalize to 0-360
}

const isValidLatLng = (point: LatLng | null | undefined): point is LatLng =>
  !!point &&
  typeof point.lat === 'number' &&
  Number.isFinite(point.lat) &&
  point.lat >= -90 &&
  point.lat <= 90 &&
  typeof point.lng === 'number' &&
  Number.isFinite(point.lng);

/**
 * Where to anchor the panorama search and aim the camera.
 *
 * `building.centroid` is the CLICK POINT on the map-click path, not the
 * building's middle — derive the true centre from the footprint so the camera
 * target AND the panorama search anchor on the building itself (mirrors the
 * hero map). Fall back to centroid, then the first address (top-level or under
 * `properties`), the same chain the location map and like requests use.
 *
 * Every field is optional: the stored `/building-page` document carries no
 * `addresses` or `centroid` and its Mapbox-resolved `geometry` can be null,
 * so a building reached through the listing view may arrive with only one of
 * these populated. Indexing `addresses[0]` on such a document threw, which
 * surfaced as "Failed to load Street View" for buildings with perfectly good
 * imagery.
 */
export const streetViewTarget = (
  building: BuildingWithConvertedAddresses,
): LatLng | null => {
  const fromFootprint = centroidOf(building.geometry);
  if (isValidLatLng(fromFootprint)) return fromFootprint;
  const fromBuilding = getBuildingLikeCoordinates(building);
  return isValidLatLng(fromBuilding) ? fromBuilding : null;
};

// `window.google.maps` exists as soon as the API's bootstrap runs, before the
// classes do — a bare `window.google.maps` check passed during that window and
// `new google.maps.StreetViewPanorama` then threw. Check for the classes.
const isStreetViewApiReady = (): boolean =>
  typeof window.google?.maps?.StreetViewPanorama === 'function' &&
  typeof window.google?.maps?.StreetViewService === 'function';

const ensureGoogleMapsScript = () => {
  if (
    document.getElementById(SHARED_MAPS_SCRIPT_ID) ||
    document.getElementById(OWN_MAPS_SCRIPT_ID)
  ) {
    return;
  }
  const script = document.createElement('script');
  script.id = OWN_MAPS_SCRIPT_ID;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
  script.async = true;
  script.defer = true;
  script.onerror = () => {
    // Do not leave a dead fallback script behind: its id would make every
    // later mount believe a load was still in progress and wait 15 seconds.
    // One component may have injected it while several Street Views are
    // waiting, so notify every waiter rather than only the creator.
    script.remove();
    window.dispatchEvent(new Event(OWN_MAPS_SCRIPT_ERROR_EVENT));
  };
  document.head.appendChild(script);
};

const StreetView: React.FC<StreetViewProps> = ({ selectedBuilding }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const streetViewRef = useRef<HTMLDivElement>(null);
  // One panorama per mount. When the building under this component changes —
  // a new building, or the stored document replacing the seed the view was
  // painted from — re-target this instance instead of stacking a second
  // panorama in the same element and racing their callbacks.
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // The Street View section sits below the fold. Booting the Google panorama
  // (heavy JS + dozens of tile requests) on mount blocks the main thread and
  // delays everything else (cards, icons). Defer it until it scrolls near view.
  const [shouldLoad, setShouldLoad] = React.useState(false);

  useEffect(() => {
    if (shouldLoad) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    // Reset before re-init so a stale "not available" error from the previous
    // building can't keep the panorama hidden when a new building succeeds.
    setError(null);
    setIsLoading(true);

    // Flipped by cleanup. The building can swap under this effect while the
    // panorama lookup is in flight (seed → stored document, or a new building),
    // and a superseded run's results must not touch state or the panorama.
    let cancelled = false;

    // Initialize Street View when the section is near the viewport.
    const initStreetView = () => {
      if (cancelled || !streetViewRef.current) return;

      try {
        const buildingCentroid = streetViewTarget(selectedBuilding);
        if (!buildingCentroid) {
          setError('Building location not available');
          setIsLoading(false);
          return;
        }

        const panorama =
          panoramaRef.current ??
          new google.maps.StreetViewPanorama(streetViewRef.current, {
            addressControl: true,
            fullscreenControl: true,
            motionTracking: false,
            motionTrackingControl: false,
            showRoadLabels: true,
            visible: true,
          });
        panoramaRef.current = panorama;

        // Special case: hardcoded panorama for specific building
        if (selectedBuilding.id === '256a2dba') {
          panorama.setPano('m3mog8v3kkv6bBecV_hUSw');
          panorama.setPov({ heading: 177.79, pitch: 0 });
          panorama.setZoom(1);
          setIsLoading(false);
          return;
        }

        // Search for the nearest panorama to the building centre (not the
        // clicked/neighbour address, which can sit on the wrong street).
        const searchLocation = buildingCentroid;

        // Point at the building straight away; the lookup below swaps in the
        // best panorama and turns the camera once it lands.
        panorama.setPosition(searchLocation);
        panorama.setPov({ heading: 0, pitch: 0 });
        panorama.setZoom(1);

        const streetViewService = new google.maps.StreetViewService();

        /**
         * Sets the panorama to the found location and calculates the correct heading
         * to point the camera toward the building centroid.
         */
        const applyPanoramaWithHeading = (
          data: google.maps.StreetViewPanoramaData,
        ) => {
          panorama.setPano(data.location!.pano!);

          // Get the panorama's actual location
          const panoLatLng = data.location!.latLng!;
          const panoLocation = {
            lat: panoLatLng.lat(),
            lng: panoLatLng.lng(),
          };

          // Calculate heading from panorama location to building centroid
          const heading = computeHeadingToTarget(
            panoLocation,
            buildingCentroid,
          );

          // Apply the computed heading so camera faces the building
          panorama.setPov({ heading, pitch: 0 });
        };

        // Search for outdoor Street View panorama near the building
        streetViewService.getPanorama(
          {
            location: searchLocation,
            radius: 100,
            preference: google.maps.StreetViewPreference.NEAREST,
            sources: [google.maps.StreetViewSource.OUTDOOR],
          },
          (data, status) => {
            if (cancelled) return;
            if (status === google.maps.StreetViewStatus.OK && data?.location) {
              // Official Google Street View is available
              applyPanoramaWithHeading(data);
              setIsLoading(false);
            } else {
              // No official outdoor Street View available, try fallback with any source
              streetViewService.getPanorama(
                {
                  location: searchLocation,
                  radius: 100,
                  preference: google.maps.StreetViewPreference.NEAREST,
                },
                (fallbackData, fallbackStatus) => {
                  if (cancelled) return;
                  if (
                    fallbackStatus === google.maps.StreetViewStatus.OK &&
                    fallbackData?.location
                  ) {
                    // Fallback panorama found (may include user-contributed content)
                    applyPanoramaWithHeading(fallbackData);
                    setIsLoading(false);
                  } else {
                    // No Street View available at all
                    setError('Street View not available at this location');
                    setIsLoading(false);
                  }
                },
              );
            }
          },
        );
      } catch (err) {
        console.error('Error initializing Street View:', err);
        setError('Failed to load Street View');
        setIsLoading(false);
      }
    };

    if (isStreetViewApiReady()) {
      initStreetView();
      return () => {
        cancelled = true;
      };
    }

    // The API is not usable yet: either the shared provider's load is still in
    // flight (possibly mid-bootstrap) or nothing has requested it. Make sure
    // one request exists, then wait for the classes rather than for any
    // particular loader's callback — the shared loader resolves on its own
    // schedule and this component has no say in it.
    let poll: number | undefined;
    let fallbackLoadFailed = false;
    const handleFallbackLoadError = () => {
      fallbackLoadFailed = true;
      if (poll !== undefined) window.clearInterval(poll);
      if (cancelled) return;
      setError('Failed to load Street View');
      setIsLoading(false);
    };
    window.addEventListener(
      OWN_MAPS_SCRIPT_ERROR_EVENT,
      handleFallbackLoadError,
    );
    ensureGoogleMapsScript();
    const startedAt = Date.now();
    if (!fallbackLoadFailed) {
      poll = window.setInterval(() => {
        if (isStreetViewApiReady()) {
          window.clearInterval(poll);
          initStreetView();
          return;
        }
        if (Date.now() - startedAt >= API_READY_TIMEOUT_MS) {
          window.clearInterval(poll);
          if (cancelled) return;
          setError('Failed to load Street View');
          setIsLoading(false);
        }
      }, API_READY_POLL_MS);
    }

    return () => {
      cancelled = true;
      if (poll !== undefined) window.clearInterval(poll);
      window.removeEventListener(
        OWN_MAPS_SCRIPT_ERROR_EVENT,
        handleFallbackLoadError,
      );
    };
  }, [selectedBuilding, shouldLoad]);

  return (
    <div className="street-view-container" ref={containerRef}>
      {shouldLoad && isLoading && (
        <div className="street-view-loading">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className="street-view-error">
          <p>{error}</p>
        </div>
      )}
      <div
        ref={streetViewRef}
        className="street-view-panorama"
        style={{ display: isLoading || error ? 'none' : 'block' }}
      />
    </div>
  );
};

export default StreetView;
